// ignore-string-externalization

import React, { useEffect, useState } from 'react';

interface WebVitalsMetrics {
  fcp?: number;
  lcp?: number;
  cls?: number;
  fid?: number;
  ttfb?: number;
  inp?: number;
}

interface PerformanceMonitorProps {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  defaultVisible?: boolean;
}

// Helper functions for metric colors based on Core Web Vitals thresholds
const getFcpColor = (fcp?: number): string => {
  if (!fcp) return '#888';
  if (fcp < 1800) return '#0f0'; // Good
  if (fcp < 3000) return '#ff0'; // Needs improvement
  return '#f00'; // Poor
};

const getLcpColor = (lcp?: number): string => {
  if (!lcp) return '#888';
  if (lcp < 2500) return '#0f0'; // Good
  if (lcp < 4000) return '#ff0'; // Needs improvement
  return '#f00'; // Poor
};

const getClsColor = (cls?: number): string => {
  if (cls === undefined) return '#888';
  if (cls < 0.1) return '#0f0'; // Good
  if (cls < 0.25) return '#ff0'; // Needs improvement
  return '#f00'; // Poor
};

const getFidColor = (fid?: number): string => {
  if (!fid) return '#888';
  if (fid < 100) return '#0f0'; // Good
  if (fid < 300) return '#ff0'; // Needs improvement
  return '#f00'; // Poor
};

const getTtfbColor = (ttfb?: number): string => {
  if (!ttfb) return '#888';
  if (ttfb < 800) return '#0f0'; // Good
  if (ttfb < 1800) return '#ff0'; // Needs improvement
  return '#f00'; // Poor
};

const getInpColor = (inp?: number): string => {
  if (!inp) return '#888';
  if (inp < 200) return '#0f0'; // Good
  if (inp < 500) return '#ff0'; // Needs improvement
  return '#f00'; // Poor
};

const getPositionStyles = (position: string) => {
  const base = {
    position: 'fixed' as const,
    zIndex: 999999,
  };

  switch (position) {
    case 'top-left':
      return { ...base, top: 10, left: 10 };
    case 'bottom-right':
      return { ...base, bottom: 10, right: 10 };
    case 'bottom-left':
      return { ...base, bottom: 10, left: 10 };
    case 'top-right':
    default:
      return { ...base, top: 10, right: 10 };
  }
};

export const PerformanceMonitor: React.FC<PerformanceMonitorProps> = ({
  position = 'top-right',
  defaultVisible = true,
}) => {
  const [metrics, setMetrics] = useState<WebVitalsMetrics>({});
  const [isVisible, setIsVisible] = useState(defaultVisible);
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    // Keyboard shortcut to toggle visibility: Ctrl/Cmd + Shift + P
    const handleKeyPress = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'P') {
        e.preventDefault();
        setIsVisible(v => !v);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  useEffect(() => {
    const observers: PerformanceObserver[] = [];

    // Measure FCP (First Contentful Paint)
    try {
      const fcpObserver = new PerformanceObserver(list => {
        for (const entry of list.getEntries()) {
          if (entry.name === 'first-contentful-paint') {
            setMetrics(m => ({ ...m, fcp: entry.startTime }));
          }
        }
      });
      fcpObserver.observe({ type: 'paint', buffered: true });
      observers.push(fcpObserver);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[PerformanceMonitor] FCP observer error:', e);
    }

    // Measure LCP (Largest Contentful Paint)
    try {
      const lcpObserver = new PerformanceObserver(list => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1] as any;
        setMetrics(m => ({
          ...m,
          lcp: lastEntry.renderTime || lastEntry.loadTime,
        }));
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      observers.push(lcpObserver);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[PerformanceMonitor] LCP observer error:', e);
    }

    // Measure CLS (Cumulative Layout Shift)
    try {
      let clsValue = 0;
      const clsObserver = new PerformanceObserver(list => {
        for (const entry of list.getEntries()) {
          if (!(entry as any).hadRecentInput) {
            clsValue += (entry as any).value;
          }
        }
        setMetrics(m => ({ ...m, cls: clsValue }));
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });
      observers.push(clsObserver);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[PerformanceMonitor] CLS observer error:', e);
    }

    // Measure FID (First Input Delay) - deprecated in favor of INP but still useful
    try {
      const fidObserver = new PerformanceObserver(list => {
        for (const entry of list.getEntries()) {
          const fidEntry = entry as any;
          const fid = fidEntry.processingStart - fidEntry.startTime;
          setMetrics(m => ({ ...m, fid }));
        }
      });
      fidObserver.observe({ type: 'first-input', buffered: true });
      observers.push(fidObserver);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[PerformanceMonitor] FID observer error:', e);
    }

    // Measure INP (Interaction to Next Paint) - newer metric replacing FID
    try {
      const inpObserver = new PerformanceObserver(list => {
        let maxDuration = 0;
        for (const entry of list.getEntries()) {
          const eventEntry = entry as any;
          const duration = eventEntry.duration;
          if (duration > maxDuration) {
            maxDuration = duration;
          }
        }
        if (maxDuration > 0) {
          setMetrics(m => ({ ...m, inp: maxDuration }));
        }
      });
      inpObserver.observe({
        type: 'event',
        buffered: true,
        // @ts-ignore - durationThreshold is part of newer spec, not in types yet
        durationThreshold: 16,
      } as PerformanceObserverInit);
      observers.push(inpObserver);
    } catch (e) {
      // Browser might not support this observer (newer metric)
      // eslint-disable-next-line no-console
      console.warn('[PerformanceMonitor] INP not supported:', e);
    }

    // Measure TTFB (Time to First Byte)
    try {
      const navigationEntry = performance.getEntriesByType(
        'navigation',
      )[0] as any;
      if (navigationEntry) {
        const ttfb =
          navigationEntry.responseStart - navigationEntry.requestStart;
        setMetrics(m => ({ ...m, ttfb }));
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[PerformanceMonitor] TTFB error:', e);
    }

    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, []);

  if (!isVisible) return null;

  if (isMinimized) {
    return (
      <div
        style={{
          ...getPositionStyles(position),
          background: 'rgba(0, 0, 0, 0.9)',
          color: 'white',
          padding: '8px 12px',
          borderRadius: 8,
          fontSize: 12,
          fontFamily: 'monospace',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => setIsMinimized(false)}
        title="Click to expand (or use Cmd/Ctrl+Shift+P to toggle)"
      >
        📊 Perf
      </div>
    );
  }

  return (
    <div
      style={{
        ...getPositionStyles(position),
        background: 'rgba(0, 0, 0, 0.9)',
        color: 'white',
        padding: '12px 16px',
        borderRadius: 8,
        fontSize: 12,
        fontFamily: 'monospace',
        minWidth: 240,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <div style={{ fontWeight: 'bold' }}>⚡ Core Web Vitals</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setIsMinimized(true)}
            style={{
              background: 'none',
              border: 'none',
              color: '#aaa',
              cursor: 'pointer',
              fontSize: 14,
              padding: 0,
            }}
            title="Minimize"
          >
            −
          </button>
          <button
            onClick={() => setIsVisible(false)}
            style={{
              background: 'none',
              border: 'none',
              color: '#aaa',
              cursor: 'pointer',
              fontSize: 14,
              padding: 0,
            }}
            title="Close (Cmd/Ctrl+Shift+P to toggle)"
          >
            ×
          </button>
        </div>
      </div>

      {/* Core Web Vitals */}
      <div style={{ marginBottom: 12 }}>
        <MetricRow
          label="LCP"
          value={metrics.lcp}
          color={getLcpColor(metrics.lcp)}
          format="time"
          tooltip="Largest Contentful Paint - Good: <2.5s"
        />
        <MetricRow
          label="FCP"
          value={metrics.fcp}
          color={getFcpColor(metrics.fcp)}
          format="time"
          tooltip="First Contentful Paint - Good: <1.8s"
        />
        <MetricRow
          label="CLS"
          value={metrics.cls}
          color={getClsColor(metrics.cls)}
          format="number"
          tooltip="Cumulative Layout Shift - Good: <0.1"
        />
      </div>

      {/* Additional metrics */}
      <div style={{ borderTop: '1px solid #444', paddingTop: 8 }}>
        <MetricRow
          label="TTFB"
          value={metrics.ttfb}
          color={getTtfbColor(metrics.ttfb)}
          format="time"
          tooltip="Time to First Byte - Good: <800ms"
        />
        {metrics.fid !== undefined && (
          <MetricRow
            label="FID"
            value={metrics.fid}
            color={getFidColor(metrics.fid)}
            format="time"
            tooltip="First Input Delay - Good: <100ms"
          />
        )}
        {metrics.inp !== undefined && (
          <MetricRow
            label="INP"
            value={metrics.inp}
            color={getInpColor(metrics.inp)}
            format="time"
            tooltip="Interaction to Next Paint - Good: <200ms"
          />
        )}
      </div>

      <div
        style={{
          fontSize: 10,
          marginTop: 8,
          color: '#666',
          borderTop: '1px solid #333',
          paddingTop: 8,
        }}
      >
        <div>🟢 Good | 🟡 Needs Work | 🔴 Poor</div>
        <div style={{ marginTop: 4 }}>
          Toggle: <kbd>Cmd/Ctrl + Shift + P</kbd>
        </div>
      </div>
    </div>
  );
};

// Helper component for metric rows
const MetricRow: React.FC<{
  label: string;
  value?: number;
  color: string;
  format: 'time' | 'number';
  tooltip?: string;
}> = ({ label, value, color, format, tooltip }) => {
  let displayValue = '...';
  if (value !== undefined) {
    if (format === 'time') {
      displayValue =
        value < 1000
          ? `${Math.round(value)}ms`
          : `${(value / 1000).toFixed(2)}s`;
    } else {
      displayValue = value.toFixed(3);
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 4,
        alignItems: 'center',
        zIndex: 999999,
      }}
      title={tooltip}
    >
      <span>{label}:</span>
      <span style={{ color, fontWeight: 'bold' }}>{displayValue}</span>
    </div>
  );
};

// Only render in development
export const DevPerformanceMonitor: React.FC<
  PerformanceMonitorProps
> = props => {
  // Early return for production - no logging
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  return <PerformanceMonitor {...props} />;
};
